<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>لوحة معلومات المهام المخصصة 168</b-card-title>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="serviceTypeId"
          :options="serviceTypeOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="fetchStatistics"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          :disabled="isLoading"
          @click="fetchStatistics"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :class="{ 'animate-spin': isLoading }"
          />
        </b-button>
      </div>
    </b-card-header>
    <b-card-body v-if="statistics">
      <b-row>
        <!-- Overall Statistics Cards -->
        <b-col
          cols="12"
          class="mb-3"
        >
          <b-row>
            <b-col
              md="3"
              sm="6"
              class="mb-2"
            >
              <b-card class="text-center stat-card">
                <h3 class="mb-0 text-primary">
                  {{ statistics.total_projects }}
                </h3>
                <small class="text-muted">إجمالي المشاريع</small>
              </b-card>
            </b-col>
            <b-col
              md="3"
              sm="6"
              class="mb-2"
            >
              <b-card class="text-center stat-card">
                <h3 class="mb-0 text-info">
                  {{ statistics.total_custom_tasks }}
                </h3>
                <small class="text-muted">إجمالي المهام المخصصة</small>
              </b-card>
            </b-col>
            <b-col
              md="3"
              sm="6"
              class="mb-2"
            >
              <b-card class="text-center stat-card">
                <h3 class="mb-0 text-success">
                  {{ statistics.total_completed_tasks }}
                </h3>
                <small class="text-muted">المهام المكتملة</small>
              </b-card>
            </b-col>
            <b-col
              md="3"
              sm="6"
              class="mb-2"
            >
              <b-card class="text-center stat-card">
                <h3
                  class="mb-0"
                  :class="getCompletionPercentageClass(statistics.overall_completion_percentage)"
                >
                  {{ statistics.overall_completion_percentage.toFixed(1) }}%
                </h3>
                <small class="text-muted">نسبة الإنجاز</small>
              </b-card>
            </b-col>
          </b-row>
        </b-col>

        <!-- Selected Tasks Status Summary -->
        <b-col
          lg="6"
          md="12"
          class="mb-3"
        >
          <h5 class="mb-2">
            حالة المهام المحددة (مهمة واحدة لكل مشروع)
          </h5>
          <vue-apex-charts
            type="donut"
            height="300"
            :options="selectedTasksStatusChartOptions"
            :series="selectedTasksStatusChartSeries"
          />
          <div class="mt-2">
            <div
              v-for="(count, status) in statistics.selected_tasks_status_summary"
              :key="status"
              class="d-flex justify-content-between align-items-center mb-1"
            >
              <span>{{ getStatusLabel(status) }}</span>
              <b-badge :variant="getStatusBadgeVariant(status)">
                {{ count }}
              </b-badge>
            </div>
          </div>
        </b-col>

        <!-- All Tasks Status Summary -->
        <b-col
          lg="6"
          md="12"
          class="mb-3"
        >
          <h5 class="mb-2">
            حالة جميع المهام
          </h5>
          <vue-apex-charts
            type="donut"
            height="300"
            :options="allTasksStatusChartOptions"
            :series="allTasksStatusChartSeries"
          />
          <div class="mt-2">
            <div
              v-for="(count, status) in statistics.all_tasks_status_summary"
              :key="status"
              class="d-flex justify-content-between align-items-center mb-1"
            >
              <span>{{ getStatusLabel(status) }}</span>
              <b-badge :variant="getStatusBadgeVariant(status)">
                {{ count }}
              </b-badge>
            </div>
          </div>
        </b-col>

        <!-- Custom Task Distribution -->
        <b-col
          cols="12"
          class="mb-3"
        >
          <h5 class="mb-2">
            توزيع المهام المحددة حسب نوع المهمة
          </h5>
          <b-table
            :items="customTaskDistributionItems"
            :fields="distributionTableFields"
            responsive
            class="mb-0"
            small
            striped
            hover
          >
            <template #cell(custom_task_name)="data">
              <strong>{{ data.value || `المهمة المخصصة ${data.item.custom_task_id}` }}</strong>
            </template>
            <template #cell(count)="data">
              <b-badge variant="primary">
                {{ data.value }} مشروع
              </b-badge>
            </template>
            <template #cell(status_breakdown)="data">
              <div class="d-flex flex-wrap gap-1 justify-content-center">
                <b-badge
                  v-if="data.value.pending > 0"
                  variant="secondary"
                  class="mr-1"
                >
                  قيد الانتظار: {{ data.value.pending }}
                </b-badge>
                <b-badge
                  v-if="data.value.in_progress > 0"
                  variant="info"
                  class="mr-1"
                >
                  جار: {{ data.value.in_progress }}
                </b-badge>
                <b-badge
                  v-if="data.value.completed > 0"
                  variant="success"
                  class="mr-1"
                >
                  تم: {{ data.value.completed }}
                </b-badge>
                <b-badge
                  v-if="data.value.blocked > 0"
                  variant="danger"
                  class="mr-1"
                >
                  تعذر: {{ data.value.blocked }}
                </b-badge>
                <span
                  v-if="!data.value || Object.values(data.value).every(v => v === 0)"
                  class="text-muted"
                >
                  لا توجد بيانات
                </span>
              </div>
            </template>
          </b-table>
        </b-col>

        <!-- Comparison Section -->
        <b-col cols="12">
          <h5 class="mb-2">
            مقارنة: المهام المحددة مقابل جميع المهام
          </h5>
          <b-row>
            <b-col md="6">
              <b-card class="comparison-card">
                <h6 class="mb-3">
                  المهام المحددة (مهمة واحدة لكل مشروع)
                </h6>
                <div
                  v-for="(count, status) in statistics.selected_tasks_status_summary"
                  :key="status"
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <span>{{ getStatusLabel(status) }}</span>
                  <b-badge :variant="getStatusBadgeVariant(status)">
                    {{ count }}
                  </b-badge>
                </div>
              </b-card>
            </b-col>
            <b-col md="6">
              <b-card class="comparison-card">
                <h6 class="mb-3">
                  جميع المهام
                </h6>
                <div
                  v-for="(count, status) in statistics.all_tasks_status_summary"
                  :key="status"
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <span>{{ getStatusLabel(status) }}</span>
                  <b-badge :variant="getStatusBadgeVariant(status)">
                    {{ count }}
                  </b-badge>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-col>
        <!-- Custom Task Distribution -->
        <b-col
          cols="12"
          class="mb-3"
        >
          <h5 class="mb-2">
            احصائيات المنصة
          </h5>
          <b-table
            :items="LandSurveyStatistics"
            :fields="LandSurveyStatisticsFields"
            responsive
            class="mb-0"
            small
            striped
            hover
          >
            <template #cell(breakDown)="data">
              <div class="d-flex flex-wrap gap-1 justify-content-center">
                <template v-for="(count, status) in data.value">
                  <b-badge
                    v-if="count > 0 && status"
                    :key="status"
                    :variant="getBadgeVariant(status, data.item.name)"
                    class="mr-1 mb-1"
                  >
                    {{ status }}: {{ count }}
                  </b-badge>
                </template>
                <span
                  v-if="!data.value || Object.values(data.value).every(v => v === 0 || v === '')"
                  class="text-muted"
                >
                  لا توجد بيانات
                </span>
              </div>
            </template>
          </b-table>
        </b-col>

      </b-row>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          جاري تحميل البيانات...
        </p>
      </div>
    </b-card-body>

    <!-- Error State -->
    <b-card-body v-else-if="error">
      <div class="text-center py-3">
        <b-alert
          variant="danger"
          show
        >
          {{ error }}
        </b-alert>
        <b-button
          variant="primary"
          size="sm"
          @click="fetchStatistics"
        >
          إعادة المحاولة
        </b-button>
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <p class="text-muted">
          لا توجد بيانات متاحة
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BTable,
  BBadge,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'
import Projects from '@/api/projects/projects'

export default {
  name: 'Dashboard168Widget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormSelect,
    BTable,
    BBadge,
    BSpinner,
    BAlert,
    VueApexCharts,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      statistics: null,
      isLoading: false,
      error: null,
      serviceTypeId: 17,
      serviceTypeOptions: [
        { value: 17, text: 'الرفع المساحي لمنصة التقنين 168 - التعمير' },
        //   { value: 104, text: 'التعمير' },
      ],
      LandSurveyStatisticsFields: [
        { key: 'name', label: 'الحالة' },
        { key: 'breakDown', label: 'تفصيل الحالة',thClass:'text-center' },

      ],
      distributionTableFields: [
        { key: 'custom_task_name', label: 'اسم المهمة المخصصة' },
        {
          key: 'count', label: 'الاجمالي', thClass: 'text-center', tdClass: 'text-center',
        },
        {
          key: 'status_breakdown', label: 'تفصيل الحالة', thClass: 'text-center', tdClass: 'text-center',
        },

      ],
    }
  },
  computed: {
    selectedTasksStatusChartSeries() {
      if (!this.statistics?.selected_tasks_status_summary) return []
      return Object.values(this.statistics.selected_tasks_status_summary)
    },
    selectedTasksStatusChartOptions() {
      return {
        chart: {
          type: 'donut',
        },
        labels: this.getStatusLabels(Object.keys(this.statistics?.selected_tasks_status_summary || {})),
        colors: this.getStatusColors(Object.keys(this.statistics?.selected_tasks_status_summary || {})),
        legend: {
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
          formatter: val => `${val.toFixed(1)}%`,
        },
      }
    },
    allTasksStatusChartSeries() {
      if (!this.statistics?.all_tasks_status_summary) return []
      return Object.values(this.statistics.all_tasks_status_summary)
    },
    allTasksStatusChartOptions() {
      return {
        chart: {
          type: 'donut',
        },
        labels: this.getStatusLabels(Object.keys(this.statistics?.all_tasks_status_summary || {})),
        colors: this.getStatusColors(Object.keys(this.statistics?.all_tasks_status_summary || {})),
        legend: {
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
          formatter: val => `${val.toFixed(1)}%`,
        },
      }
    },
    LandSurveyStatistics() {
      if (!this.statistics?.landsurvey_order_statistics) return []
      return this.statistics.landsurvey_order_statistics
    },
    customTaskDistributionItems() {
      if (!this.statistics?.selected_tasks_by_custom_task_id) return []
      return Object.entries(this.statistics.selected_tasks_by_custom_task_id)
        .map(([custom_task_id, data]) => ({
          custom_task_id,
          custom_task_name: data.custom_task_name,
          count: data.count,
          status_breakdown: data.status_breakdown || {
            pending: 0,
            in_progress: 0,
            completed: 0,
            blocked: 0,
          },
        }))

      // .sort((a, b) => b.count - a.count)
    },
  },
  mounted() {
    this.fetchStatistics()
  },
  methods: {
    getBadgeVariant(status, categoryName) {
      // Remove empty strings and normalize
      if (!status || status === '') return 'secondary';
      
      // Define color schemes based on category
      const variants = {
        // For "حالة المراجعة"
        'جاري المراجعة': 'secondary',
        'جاري الرفع': 'info',
        'مقبول': 'success',
        'لم يتم المعاينة': 'warning',
        'تم اعادة المراجعة': 'primary',
        'مرفوض': 'danger',
        
        // For "حالة الرفع المساحي"
        'تم الرفع المساحي': 'success',
        'لم يتم الرفع المساحي': 'warning',
        'تعذر الرفع المساحى': 'danger',
        
        // For "مراجعة الجيوديسيا"
        'مقبول من الجيودسيا': 'success',
        'لم يتم إرسال الطلب للجيودسيا': 'warning',
        'مرفوض من الجيودسيا': 'danger'
      };
      
      return variants[status] || 'secondary';
    },
    async fetchStatistics() {
      this.isLoading = true
      this.error = null

      try {
        const projectsApi = new Projects()
        const response = await projectsApi.getDashboard168Statistics(this.serviceTypeId)

        if (response.success && response.data?.overall_statistics) {
          this.statistics = response.data.overall_statistics
        } else if (response.data?.overall_statistics) {
          this.statistics = response.data.overall_statistics
        } else {
          this.error = 'لم يتم العثور على بيانات'
        }
      } catch (err) {
        console.error('Error fetching Dashboard 168 statistics:', err)
        this.error = err.response?.data?.message || 'حدث خطأ أثناء تحميل البيانات'
      } finally {
        this.isLoading = false
      }
    },
    getStatusLabel(status) {
      const labels = {
        pending: 'قيد الانتظار',
        in_progress: 'جار',
        completed: 'تم',
        review: 'قيد المراجعة',
        blocked: 'تعذر',
        client_pending: 'في انتظار العميل',
      }
      return labels[status] || status
    },
    getStatusBadgeVariant(status) {
      const variants = {
        pending: 'secondary',
        in_progress: 'info',
        completed: 'success',
        review: 'warning',
        blocked: 'danger',
        client_pending: 'warning',
      }
      return variants[status] || 'secondary'
    },
    getStatusColors(statuses) {
      const colorMap = {
        pending: '#6c757d',
        in_progress: '#17a2b8',
        completed: '#28a745',
        review: '#ffc107',
        blocked: '#dc3545',
        client_pending: '#fd7e14',
      }
      return statuses.map(status => colorMap[status] || '#6c757d')
    },
    getStatusLabels(statuses) {
      return statuses.map(status => this.getStatusLabel(status))
    },
    getCompletionPercentageClass(percentage) {
      if (percentage >= 80) return 'text-success'
      if (percentage >= 50) return 'text-warning'
      return 'text-danger'
    },
  },
}
</script>

<style scoped>
.stat-card {
  border-left: 3px solid #7367f0;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.comparison-card {
  height: 100%;
}

.gap-1 {
  gap: 0.25rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
